import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Typography,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import LibraryMusicRoundedIcon from '@mui/icons-material/LibraryMusicRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import { Menu } from 'Components/RoadmapComponents/Menu';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import './studentAssignments.css';
import TeacherConnections, { TeacherRequest } from './TeacherConnections';
import { useDispatch, useSelector } from 'react-redux';
import { MainAppReducer } from 'Types';
import { RootState } from 'Reducers';
import * as Immutable from 'immutable';
import { resolvePlayRoute, type CompletedChapters } from 'Utils/tutorialCompletion';
import * as appActions from 'Actions/app';
import * as repertoireActions from 'Actions/repertoire';

export type StudentAssignmentStatus =
  | 'not_started'
  | 'in_progress'
  | 'practiced'
  | 'completed'
  | 'overdue'
  | 'canceled';

export interface StudentAssignmentItem {
  id: string;
  instructorName: string;
  targetId: string;
  targetName: string;
  targetSubtitle: string;
  type: 'roadmap_level' | 'repertoire_piece';
  dueDate: string | null;
  status: StudentAssignmentStatus;
  notes: string | null;
  progress: number;
  progressLabel: string;
  accessStatus: 'trial' | 'active' | 'unavailable';
  createdAt: string;
}

// eslint-disable-next-line react-refresh/only-export-components -- reused by the dev-only visual route
export const visualStudentAssignments: StudentAssignmentItem[] = [
  {
    id: 'assignment-student-1',
    instructorName: 'Ms. Rivera',
    targetId: 'level-18',
    targetName: 'Level 18',
    targetSubtitle: 'Eighth notes and stepwise motion',
    type: 'roadmap_level',
    dueDate: '2026-09-05',
    status: 'in_progress',
    notes: 'Keep the pulse steady and finish all three tiers.',
    progress: 55,
    progressLabel: 'Tier 2 of 3',
    accessStatus: 'active',
    createdAt: '2026-08-29T17:00:00.000Z',
  },
  {
    id: 'assignment-student-2',
    instructorName: 'Ms. Rivera',
    targetId: 'rep-bach-minuet-g',
    targetName: 'Minuet in G Major',
    targetSubtitle: 'J. S. Bach · BWV Anh. 114',
    type: 'repertoire_piece',
    dueDate: '2026-08-30',
    status: 'overdue',
    notes: 'Play through once, then revisit measures 9–16.',
    progress: 0,
    progressLabel: 'Not started',
    accessStatus: 'active',
    createdAt: '2026-08-23T17:00:00.000Z',
  },
  {
    id: 'assignment-student-3',
    instructorName: 'Ms. Rivera',
    targetId: 'rep-schumann-melody',
    targetName: 'Melody',
    targetSubtitle: 'Robert Schumann · Op. 68, No. 1',
    type: 'repertoire_piece',
    dueDate: '2026-09-08',
    status: 'practiced',
    notes: 'Shape each four-bar phrase and listen for balance.',
    progress: 100,
    progressLabel: 'Complete play-through recorded',
    accessStatus: 'active',
    createdAt: '2026-08-27T17:00:00.000Z',
  },
];

const statusMeta: Record<
  StudentAssignmentStatus,
  { label: string; color: string; priority: number }
> = {
  overdue: { label: 'Overdue', color: '#f7c85d', priority: 0 },
  in_progress: { label: 'In progress', color: '#9eafff', priority: 1 },
  not_started: { label: 'Not started', color: '#b8c7d8', priority: 2 },
  practiced: { label: 'Practiced', color: '#5eead4', priority: 3 },
  completed: { label: 'Completed', color: '#86efac', priority: 4 },
  canceled: { label: 'Canceled', color: '#94a3b8', priority: 5 },
};

const formatDueDate = (value: string | null) => {
  if (!value) return 'No due date';
  return `Due ${new Date(`${value}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};

const StudentAssignments = ({
  fixtureAssignments,
}: {
  fixtureAssignments?: StudentAssignmentItem[];
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const levels = useSelector((state: MainAppReducer) => state.mainAppReducer.levelData);
  const progress = useSelector((state: MainAppReducer) => state.mainAppReducer.userLevelData);
  const completedTutorials = useSelector((state: RootState) => state.tutorialReducer?.completed);
  const auth = useAuthWrapperContext();
  const [assignments, setAssignments] = useState<StudentAssignmentItem[]>(fixtureAssignments || []);
  const [loading, setLoading] = useState(!fixtureAssignments);
  const [error, setError] = useState('');
  const request = useCallback<TeacherRequest>(
    async (method, path, body) => {
      const token = await auth?.currentAuthToken();
      const response = await axios({
        method,
        url: `${process.env.REACT_APP_BACKEND_URL}/api/v1/student${path}`,
        data: body,
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      });
      return response.data;
    },
    [auth],
  );

  const loadAssignments = useCallback(async () => {
    if (fixtureAssignments) return;
    try {
      setLoading(true);
      setError('');
      const token = await auth?.currentAuthToken();
      const response = await axios.get<
        { data?: StudentAssignmentItem[] } | StudentAssignmentItem[]
      >(`${process.env.REACT_APP_BACKEND_URL}/api/v1/student/assignments`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
      });
      const payload = Array.isArray(response.data) ? response.data : response.data.data || [];
      setAssignments(payload);
    } catch (requestError) {
      console.error('Failed to load student assignments:', requestError);
      setError('Your assignments could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [auth, fixtureAssignments]);

  useEffect(() => {
    void loadAssignments();
  }, [loadAssignments]);

  const sortedAssignments = useMemo(
    () =>
      [...assignments].sort((a, b) => {
        const statusDiff = statusMeta[a.status].priority - statusMeta[b.status].priority;
        if (statusDiff) return statusDiff;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return a.dueDate.localeCompare(b.dueDate);
      }),
    [assignments],
  );

  const openAssignment = async (assignment: StudentAssignmentItem) => {
    if (fixtureAssignments) return;
    try {
      setError('');
      const target = await request<{ type: string; targetId: string }>(
        'POST',
        `/assignments/${assignment.id}/launch`,
      );
      if (target.type === 'roadmap_level') {
        const index = levels.findIndex((level) => level.level_number === Number(target.targetId));
        if (index < 0) throw new Error('Assigned level is unavailable');
        dispatch(appActions.setLevelSelect({ levelSelect: index }));
        dispatch(appActions.fetchTutorialData());
        const levelNumber = Number(target.targetId);
        const completedTiers =
          progress.find((p) => p.level_number === levelNumber)?.highest_tier_complete || 0;
        navigate(
          resolvePlayRoute(
            levelNumber,
            completedTiers,
            Immutable.get(completedTutorials, levelNumber) as CompletedChapters,
          ),
        );
      } else {
        dispatch(
          repertoireActions.setRepertoireSelect({ repertoireIdSelect: Number(target.targetId) }),
        );
        navigate('/repertoire-play');
      }
    } catch {
      setError(
        'This assignment could not be opened. Refresh your assignments and check your MuseFlow access.',
      );
    }
  };

  const openCount = assignments.filter(
    (item) => !['practiced', 'completed', 'canceled'].includes(item.status),
  ).length;

  return (
    <Box className="student-assignments-page" data-testid="student-assignments">
      <Menu selected="Assignments" collapseToDrawer />
      <Box className="student-assignments-shell">
        <Box className="student-assignments-heading">
          <Box>
            <Chip
              icon={<AssignmentTurnedInRoundedIcon />}
              label={`${openCount} open assignment${openCount === 1 ? '' : 's'}`}
              size="small"
              className="student-assignments-count"
            />
            <Typography component="h1">Your assignments</Typography>
            <Typography className="student-assignments-subtitle">
              Practice selected by your instructor, connected directly to MuseFlow.
            </Typography>
          </Box>
        </Box>

        {!fixtureAssignments && (
          <TeacherConnections request={request} onChange={() => void loadAssignments()} />
        )}
        {error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" onClick={() => void loadAssignments()}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}
        {loading ? (
          <Box className="student-assignments-loading">
            <CircularProgress sx={{ color: '#8da0ff' }} />
          </Box>
        ) : (
          <Box className="student-assignment-list">
            {sortedAssignments.map((assignment) => {
              const meta = statusMeta[assignment.status];
              const terminal = ['practiced', 'completed', 'canceled'].includes(assignment.status);
              const unavailable = assignment.accessStatus === 'unavailable';
              const Icon =
                assignment.type === 'roadmap_level' ? RouteRoundedIcon : LibraryMusicRoundedIcon;
              return (
                <Box
                  key={assignment.id}
                  className={`student-assignment-card student-assignment-${assignment.status}`}
                >
                  <Box className="student-assignment-icon">
                    <Icon />
                  </Box>
                  <Box className="student-assignment-main">
                    <Box className="student-assignment-eyebrow">
                      <Chip
                        label={meta.label}
                        size="small"
                        sx={{
                          color: meta.color,
                          background: `${meta.color}12`,
                          border: `1px solid ${meta.color}28`,
                          fontWeight: 800,
                        }}
                      />
                      <Box className="student-assignment-due">
                        <CalendarTodayRoundedIcon />
                        {formatDueDate(assignment.dueDate)}
                      </Box>
                    </Box>
                    <Typography component="h2">{assignment.targetName}</Typography>
                    <Typography className="student-assignment-target-subtitle">
                      {assignment.targetSubtitle}
                    </Typography>
                    {assignment.notes && (
                      <Box className="student-assignment-note">
                        <PersonOutlineRoundedIcon />
                        <Typography>
                          <strong>{assignment.instructorName}:</strong> {assignment.notes}
                        </Typography>
                      </Box>
                    )}
                    <Box className="student-assignment-progress">
                      <Box>
                        <Typography>{assignment.progressLabel}</Typography>
                        <Typography>{assignment.progress}%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={assignment.progress} />
                    </Box>
                    {assignment.status === 'practiced' && (
                      <Typography className="student-assignment-explainer">
                        Practiced means a complete play-through was recorded. It is not a mastery
                        score.
                      </Typography>
                    )}
                    {unavailable && (
                      <Alert severity="warning" className="student-assignment-access-alert">
                        MuseFlow access is unavailable. Your assignment and progress are preserved.
                      </Alert>
                    )}
                  </Box>
                  <Button
                    disabled={unavailable || assignment.status === 'canceled'}
                    onClick={() => openAssignment(assignment)}
                    endIcon={<ArrowForwardRoundedIcon />}
                    variant={terminal ? 'outlined' : 'contained'}
                    className="student-assignment-action"
                  >
                    {assignment.status === 'not_started' || assignment.status === 'overdue'
                      ? 'Start'
                      : terminal
                        ? 'Practice again'
                        : 'Continue'}
                  </Button>
                </Box>
              );
            })}
            {sortedAssignments.length === 0 && (
              <Box className="student-assignments-empty">
                <AssignmentTurnedInRoundedIcon />
                <Typography component="h2">You’re all caught up</Typography>
                <Typography>New work from an accepted instructor will appear here.</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const StudentAssignmentsVisualFixture = () => (
  <StudentAssignments fixtureAssignments={visualStudentAssignments} />
);

export default StudentAssignments;
